import { useEffect, useRef, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { waysApi, type WaysClaudeEnrollment as Enrollment } from '../../services/waysApi';

// "Claude account for runners" (PO87-120): a person pastes the token `claude setup-token` printed,
// once, from this human browser session. It goes straight to Ways and is never shown again, kept
// in React state, or logged: the input is uncontrolled and cleared as soon as it is sent.
export default function WaysClaudeEnrollment() {
  const [status, setStatus] = useState<Enrollment | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const load = () => waysApi.claudeEnrollment().then(setStatus).catch((e) => setError(e?.message || 'Could not load'));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const el = input.current;
    const token = (el?.value || '').trim();
    if (el) el.value = '';
    if (!token) return;
    if (!token.startsWith('sk-ant-')) { setError('That does not look like a Claude token (sk-ant-…).'); return; }
    setBusy(true); setError('');
    try { setStatus(await waysApi.enrollClaude(token)); } catch (e) { setError(e instanceof Error ? e.message : 'Enrollment failed'); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!confirm('Revoke your Claude account for runners? Runners acting for you stop until you enroll again.')) return;
    setBusy(true); setError('');
    try { await waysApi.revokeClaude(); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Revoke failed'); }
    finally { setBusy(false); }
  };

  const when = (t?: number | null) => (t ? new Date(t * 1000).toLocaleString() : '—');

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-lg">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound size={18} className="text-accent" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Claude account for runners</h2>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Runner sessions acting for you use your own Claude account. Run <code>claude setup-token</code> in a
          terminal, then paste the token here. It is sent to Ways once and never shown again.
        </p>

        {status && (
          <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
            {status.enrolled ? (
              <>
                <div className="font-medium">
                  Enrolled{status.stale ? <span className="ml-2 text-amber-600">· stale, enroll again</span> : null}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Fingerprint <span className="font-mono">{status.fingerprint || '—'}</span> · enrolled {when(status.enrolledAt)} · last used {status.lastUsed ? when(status.lastUsed) : 'not yet'}
                  {status.expiresAt ? <> · expires {when(status.expiresAt)}</> : null}
                </div>
              </>
            ) : <div className="text-gray-500">Not enrolled.</div>}
          </div>
        )}

        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
          {status?.enrolled ? 'Replace with a new token' : 'Token'}
        </label>
        <input ref={input} type="password" autoComplete="off" spellCheck={false} placeholder="sk-ant-…"
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-mono" />
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        <div className="flex gap-2 mt-3">
          <button onClick={submit} disabled={busy} className="px-4 py-2 text-sm font-semibold bg-accent text-white rounded-lg disabled:opacity-40">
            {busy ? 'Saving…' : 'Enroll'}
          </button>
          {status?.enrolled && (
            <button onClick={revoke} disabled={busy} className="px-3 py-2 text-sm border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg">Revoke</button>
          )}
        </div>
      </div>
    </div>
  );
}
